import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Utilities for WhatsApp link generation, deflection UI styling, and QR download.
 * Shared across admin, settings, and the embedded chat widget.
 */

/** Keep in sync with backend `update-agent-settings.activity` / agent-settings schema. */
export const WHATSAPP_PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

/**
 * Whether a stored WhatsApp number can support deflection (format + at least one digit).
 */
export function isValidWhatsAppPhoneNumber(phoneNumber: string | null | undefined): boolean {
  if (!phoneNumber?.trim()) return false;
  if (!WHATSAPP_PHONE_REGEX.test(phoneNumber)) return false;
  return phoneNumber.replace(/\D/g, '').length > 0;
}

/** Strip non-digits for comparison and wa.me links. */
export function normalizeWhatsAppDigits(phoneNumber: string): string {
  return phoneNumber.replace(/\D/g, '');
}

/**
 * Header deflection control — green brand icon only, no pill/hover highlight.
 * Touch devices get a 44px hit target.
 */
export const whatsappDeflectionHeaderButtonSx: SxProps<Theme> = {
  bgcolor: 'transparent',
  border: 'none',
  borderRadius: 0,
  boxShadow: 'none',
  width: 36,
  height: 36,
  p: 0,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '@media (pointer: coarse)': {
    width: 44,
    height: 44,
  },
  '&:hover': {
    bgcolor: 'transparent',
    boxShadow: 'none',
  },
  '&.Mui-focusVisible': {
    bgcolor: 'transparent',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: 'transparent',
    boxShadow: 'none',
  },
};

/**
 * Build a WhatsApp link from a phone number.
 * Strips all non-digit characters to create a clean wa.me URL.
 * Returns null if the phone number contains no digits.
 */
export function buildWhatsAppLink(phoneNumber: string): string | null {
  const digits = phoneNumber.replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  return `https://wa.me/${digits}`;
}

/** Deflection UI requires strict format validation plus a buildable wa.me link. */
export function canShowWhatsAppDeflection(
  phoneNumber: string | null | undefined,
  deflectionEnabled: boolean | undefined,
): boolean {
  return Boolean(
    deflectionEnabled && isValidWhatsAppPhoneNumber(phoneNumber) && buildWhatsAppLink(phoneNumber ?? ''),
  );
}

/**
 * Download a canvas element as a JPEG image.
 * Used for downloading QR codes.
 *
 * @param canvasId - The DOM id of the canvas element
 * @param filename - The filename for the downloaded image (without extension)
 * @param onComplete - Optional callback after successful download
 */
export function downloadCanvasAsJpeg(
  canvasId: string,
  filename: string = 'qr-code',
  onComplete?: () => void,
): void {
  const element = document.getElementById(canvasId);
  // Verify the element exists and is actually a canvas
  if (!(element instanceof HTMLCanvasElement)) return;

  element.toBlob(
    (blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filename}.jpg`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      onComplete?.();
    },
    'image/jpeg',
    0.92,
  );
}
